/**
 * Locale-aware currency formatting built on Intl.NumberFormat.
 * Formatter instances are memoized — constructing Intl.NumberFormat is
 * expensive and prices render in long lists.
 */
const LANG_TO_LOCALE = { en: 'en', ur: 'ur-PK', ar: 'ar' }

const formatterCache = new Map()

export function getUserLocale() {
  try {
    const lang = localStorage.getItem('lang')
    if (lang && LANG_TO_LOCALE[lang]) return LANG_TO_LOCALE[lang]
  } catch (e) {
    // storage disabled — fall through
  }
  return (typeof navigator !== 'undefined' && navigator.language) || 'en'
}

function getFormatter(currencyCode, locale, round) {
  const key = `${locale}|${currencyCode}|${round ? 1 : 0}`
  let formatter = formatterCache.get(key)
  if (!formatter) {
    formatter = new Intl.NumberFormat(locale, {
      style: 'currency',
      currency: currencyCode,
      ...(round ? { minimumFractionDigits: 0, maximumFractionDigits: 0 } : {}),
    })
    formatterCache.set(key, formatter)
  }
  return formatter
}

/**
 * Format an amount (already in the target currency) for display.
 * Falls back to a plain "CODE 12.34" string if Intl rejects the
 * currency code or locale.
 */
export function formatCurrency(amount, currencyCode, { locale, round = false } = {}) {
  const resolvedLocale = locale || getUserLocale()
  try {
    return getFormatter(currencyCode, resolvedLocale, round).format(amount)
  } catch (e) {
    const value = round ? Math.round(amount).toString() : Number(amount).toFixed(2)
    return `${currencyCode} ${value}`
  }
}

/**
 * Format an amount and split it for styled rendering, e.g. a big integer
 * part with a small superscript fraction:
 *   { formatted: 'PKR 27,512.80', prefix: 'PKR ', integer: '27,512', fraction: '.80', suffix: '' }
 * Works with symbol-after locales too (the symbol lands in `suffix`).
 */
export function formatCurrencyParts(amount, currencyCode, { locale } = {}) {
  const resolvedLocale = locale || getUserLocale()
  try {
    const parts = getFormatter(currencyCode, resolvedLocale, false).formatToParts(amount)
    const result = { formatted: '', prefix: '', integer: '', fraction: '', suffix: '' }
    let stage = 'prefix'
    parts.forEach(part => {
      result.formatted += part.value
      if (part.type === 'integer' || part.type === 'group') {
        stage = 'integer'
        result.integer += part.value
      } else if (part.type === 'decimal' || part.type === 'fraction') {
        stage = 'fraction'
        result.fraction += part.value
      } else if (stage === 'prefix') {
        result.prefix += part.value
      } else {
        result.suffix += part.value
      }
    })
    return result
  } catch (e) {
    const fixed = Number(amount).toFixed(2)
    const [whole, decimal] = fixed.split('.')
    return {
      formatted: `${currencyCode} ${fixed}`,
      prefix: `${currencyCode} `,
      integer: whole,
      fraction: `.${decimal}`,
      suffix: '',
    }
  }
}
